/*
 * Delete impact analysis.
 *
 * Nothing is ever deleted in one step. The admin UI asks the server what a
 * delete would do, shows the answer, and only then sends the delete back with
 * the confirmation token the server issued. Cancelling the dialog is how the
 * user blocks the change, and it is the default: the dialog opens with nothing
 * committed.
 *
 * Why a token rather than a plain "confirm=true" flag
 * ---------------------------------------------------
 * The token is signed over a fingerprint of the impact that was shown. If
 * anything changed between the preview and the confirmation - a new article was
 * tagged with the product being deleted, someone else linked to the article
 * being removed - the fingerprint no longer matches and the server answers 409
 * rather than deleting more than the user agreed to. It also means a delete
 * cannot be issued at all without having asked what it would do first.
 *
 * The three categories a consequence can fall into:
 *
 *   cascade      the database removes it for us (ON DELETE CASCADE)
 *   cleanup      we remove or rewrite it, because the database would either
 *                refuse or leave it dangling - junction rows, inline article
 *                references in other articles' text
 *   blocker      we refuse: the delete cannot be made safe. A product raises
 *                one while any article carries it ('product-in-use') and, since
 *                S42 (2026-10-12), while any release note does
 *                ('product-in-use-by-release-notes') - the foreign keys do not
 *                cascade, and retiring the product is the way out. The dialog
 *                says "no" rather than "are you sure", and issues no token
 *
 * The cascade is still shallow: deleting an article takes its revisions and its
 * attached images, and stops. Those two are the only children of kb.Article that
 * cascade, so there is exactly one level of it to report. Everything wider -
 * junction rows, inline references in other articles' and release notes' text -
 * is a cleanup we perform explicitly and therefore have to show. A release note
 * (S42) is the same shape one table over: deleting it takes its revisions and its
 * images (kb.ReleaseNoteRevision, kb.ReleaseNoteImage), and nothing links to it,
 * so there is no cleanup to report.
 *
 * Images are worth reporting even though the database removes them for us, and
 * for a sharper reason than the revisions are: the bytes exist nowhere else. The
 * site has no blob storage and no writable disk, so an image that cascades away
 * is not recoverable from a file share afterwards - only from a database
 * restore. "3 images (1.4 MB) will be destroyed" is therefore a warning and not
 * a footnote.
 */
import { formatByteSize } from './articleImages.js';
export const IMPACT_ITEM_SAMPLE_LIMIT = 25;
/**
 * The code the image consequence of an article delete carries - the bytes are
 * the one consequence no restore short of a database point-in-time recovery can
 * undo. Exported so the code is spelled once for whatever needs to name it (the
 * confirmation fingerprint hashes codes); the dialog itself does not single it
 * out today, and renders it as any destructive consequence.
 */
export const ARTICLE_IMAGE_IMPACT_CODE = 'article-images';
/**
 * The same consequence for a release-note delete (S42, 2026-10-12). Its own code
 * because it is a different table's rows - kb.ReleaseNoteImage - and a code is
 * what the fingerprint and the performed report key by.
 */
export const RELEASE_NOTE_IMAGE_IMPACT_CODE = 'release-note-images';
/**
 * Builds the "and 3 images" line of an article's delete report, or null when the
 * article has none - a consequence with a count of zero is noise in a dialog
 * whose whole job is to be read.
 *
 * Lives in the shared package rather than in the server's impact service because
 * the wording is part of the report the browser renders, and because the total is
 * computed the same way `formatByteSize` shows it everywhere else. The server
 * supplies the rows; this decides what they mean.
 */
export function articleImageConsequence(images) {
    return imageConsequence(ARTICLE_IMAGE_IMPACT_CODE, images, 'The image bytes live in the database, attached to this article, and go with it. There ' +
        'is no file share or storage account holding a second copy - only a database restore ' +
        'could bring them back. Any other article that shows one of them by an absolute URL ' +
        'will show a broken image.');
}
/**
 * The release-note counterpart of `articleImageConsequence`: the same severity,
 * effect, count and title, worded for a release note and keyed by
 * RELEASE_NOTE_IMAGE_IMPACT_CODE. Null when the note has no images.
 */
export function releaseNoteImageConsequence(images) {
    return imageConsequence(RELEASE_NOTE_IMAGE_IMPACT_CODE, images, 'The image bytes live in the database, attached to this release note, and go with it. ' +
        'There is no file share or storage account holding a second copy - only a database ' +
        'restore could bring them back. Any article or other release note that shows one of ' +
        'them by an absolute URL will show a broken image.');
}
/** The shape both image consequences share; only the code and the detail differ. */
function imageConsequence(code, images, detail) {
    if (images.length === 0)
        return null;
    const totalBytes = images.reduce((total, image) => total + image.byteLength, 0);
    const items = images.map((image) => ({
        id: image.fileName,
        label: image.fileName,
        sublabel: formatByteSize(image.byteLength),
    }));
    return {
        code,
        severity: 'destructive',
        effect: 'cascade',
        title: `${images.length} image${images.length === 1 ? '' : 's'} (${formatByteSize(totalBytes)}) will be destroyed`,
        detail,
        count: images.length,
        items: items.slice(0, IMPACT_ITEM_SAMPLE_LIMIT),
        truncated: items.length > IMPACT_ITEM_SAMPLE_LIMIT,
    };
}
export function hasDestructiveConsequence(impact) {
    return impact.consequences.some((consequence) => consequence.severity === 'destructive');
}
export function totalAffectedRows(impact) {
    return impact.consequences.reduce((total, consequence) => total + consequence.count, 0);
}
/**
 * Sorts a consequence list the way the dialog reads best: the irreversible
 * things first, context last.
 */
export function orderConsequences(consequences) {
    const weight = {
        blocker: 0,
        destructive: 1,
        warning: 2,
        info: 3,
    };
    return [...consequences].sort((a, b) => weight[a.severity] - weight[b.severity] || b.count - a.count);
}
//# sourceMappingURL=impact.js.map